import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, User, Tag, ExternalLink, X, Filter, Plus, Minus, ChevronDown, ChevronUp, Search } from 'lucide-react';

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const postsPerPage = 6;

  // Get current tag filters from URL (support multiple tags)
  const getSelectedTags = () => {
    const tagParam = searchParams.get('tag');
    return tagParam ? tagParam.split(',').filter(Boolean) : [];
  };
  
  const selectedTags = getSelectedTags();

  // Fetch available tags for the filter dropdown
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const url = "https://newsletter.manuelabamert.com/ghost/api/content/tags/";
        const params = new URLSearchParams({
          key: "d02d48a81aa0765c8cb29cce8d",
          include: "count.posts",
          filter: "visibility:public",
          fields: "id,name,slug,count",
          limit: "all"
        });

        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          // Only show tags that have posts
          const tagsWithPosts = (data.tags || []).filter(tag => tag.count && tag.count.posts > 0);
          setAvailableTags(tagsWithPosts);
        }
      } catch (err) {
        console.error('Error fetching tags:', err);
      } finally {
        setLoadingTags(false);
      }
    };

    fetchTags();
  }, []);

  // Reset pagination when tag filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setPosts([]);
    fetchPosts(1, false);
  }, [selectedTags.join(',')]); // Use join to track array changes

  const fetchPosts = async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const url = "https://newsletter.manuelabamert.com/ghost/api/content/posts/";
      
      // Build filter string for multiple tags
      let filterString = "visibility:public";
      if (selectedTags.length > 0) {
        // Use OR logic for multiple tags: tag:tag1,tag:tag2
        const tagFilters = selectedTags.map(tag => `tag:${tag}`).join(',');
        filterString += `+${tagFilters}`;
      }
      
      const params = new URLSearchParams({
        key: "d02d48a81aa0765c8cb29cce8d",
        include: "authors,tags",
        filter: filterString,
        fields: "id,title,slug,excerpt,feature_image,feature_image_alt,published_at,reading_time,url",
        page: page.toString(),
        limit: postsPerPage.toString()
      });

      const response = await fetch(`${url}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const newPosts = data.posts || [];
      
      if (append) {
        setPosts(prevPosts => [...prevPosts, ...newPosts]);
      } else {
        setPosts(newPosts);
      }
      
      setPagination(data.meta?.pagination || null);
      setHasMore(page < (data.meta?.pagination?.pages || 1));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // Load more when user is 200px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMorePosts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, currentPage, selectedTags.join(',')]);

  const loadMorePosts = async () => {
    if (!hasMore || loadingMore) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    
    try {
      setLoadingMore(true);
      const url = "https://newsletter.manuelabamert.com/ghost/api/content/posts/";
      
      // Build filter string for multiple tags
      let filterString = "visibility:public";
      if (selectedTags.length > 0) {
        // Use OR logic for multiple tags: tag:tag1,tag:tag2
        const tagFilters = selectedTags.map(tag => `tag:${tag}`).join(',');
        filterString += `+${tagFilters}`;
      }
      
      const params = new URLSearchParams({
        key: "d02d48a81aa0765c8cb29cce8d",
        include: "authors,tags",
        filter: filterString,
        fields: "id,title,slug,excerpt,feature_image,feature_image_alt,published_at,reading_time,url",
        page: nextPage.toString(),
        limit: postsPerPage.toString()
      });

      const response = await fetch(`${url}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const newPosts = data.posts || [];
      
      setPosts(prevPosts => [...prevPosts, ...newPosts]);
      setPagination(data.meta?.pagination || null);
      setHasMore(nextPage < (data.meta?.pagination?.pages || 1));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const handleTagFilter = (tagSlugs) => {
    if (tagSlugs && tagSlugs.length > 0) {
      setSearchParams({ tag: tagSlugs.join(',') });
    } else {
      setSearchParams({});
    }
    // Scroll to top when applying filter
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleTag = (tagSlug) => {
    const newSelectedTags = selectedTags.includes(tagSlug)
      ? selectedTags.filter(tag => tag !== tagSlug) // Remove tag
      : [...selectedTags, tagSlug]; // Add tag
    
    handleTagFilter(newSelectedTags);
  };

  const clearAllFilters = () => {
    handleTagFilter([]);
  };

  const getTagDisplayNames = (tagSlugs) => {
    return tagSlugs.map(slug => {
      const tag = availableTags.find(tag => tag.slug === slug);
      return tag ? tag.name : slug;
    });
  };

  const getFilteredTags = () => {
    if (!tagSearchQuery) return availableTags;
    return availableTags.filter(tag => 
      tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
    );
  };

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error loading posts</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Manuela Bamert Newsletter</h1>
          <p className="text-lg text-gray-600">Latest articles and updates</p>
        </div>

        {/* Tag Filter Section */}
        <div className="mb-8">
          {/* Current Filters Display */}
          {selectedTags.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
              <div className="flex items-center text-sm text-gray-600 mr-2">
                <Filter className="w-4 h-4 mr-1" />
                Filtered by:
              </div>
              {selectedTags.map(tagSlug => {
                const tagName = getTagDisplayNames([tagSlug])[0];
                return (
                  <div key={tagSlug} className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    <span className="font-medium">{tagName}</span>
                    <button
                      onClick={() => toggleTag(tagSlug)}
                      className="ml-2 hover:bg-blue-200 rounded-full p-1 transition-colors duration-200"
                      title={`Remove ${tagName} filter`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              <button
                onClick={clearAllFilters}
                className="ml-2 text-gray-500 hover:text-gray-700 text-sm underline"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Tag Selector */}
          {!loadingTags && availableTags.length > 0 && (
            <div className="max-w-2xl mx-auto">
              {/* Selector Toggle Button */}
              <div className="text-center mb-4">
                <button
                  onClick={() => setShowTagSelector(!showTagSelector)}
                  className="inline-flex items-center px-6 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors duration-200"
                >
                  <Filter className="w-5 h-5 mr-2 text-gray-500" />
                  <span className="font-medium text-gray-700">
                    {showTagSelector ? 'Hide' : 'Select'} Topics
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({selectedTags.length} selected)
                  </span>
                  {showTagSelector ? (
                    <ChevronUp className="w-4 h-4 ml-2 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 ml-2 text-gray-500" />
                  )}
                </button>
              </div>

              {/* Tag Selector Panel */}
              {showTagSelector && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-6 mb-6">
                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search topics..."
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleTagFilter(availableTags.map(tag => tag.slug))}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearAllFilters}
                        className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    <span className="text-xs text-gray-500">
                      {selectedTags.length} of {availableTags.length} selected
                    </span>
                  </div>

                  {/* Tags Grid with Checkboxes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {getFilteredTags().map((tag) => {
                      const isSelected = selectedTags.includes(tag.slug);
                      return (
                        <label
                          key={tag.id}
                          className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTag(tag.slug)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                              {tag.name}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              ({tag.count.posts} {tag.count.posts === 1 ? 'post' : 'posts'})
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* No Results */}
                  {tagSearchQuery && getFilteredTags().length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No topics found for "{tagSearchQuery}"</p>
                    </div>
                  )}

                  {/* Apply Button */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {selectedTags.length > 0 
                        ? `${selectedTags.length} topic${selectedTags.length === 1 ? '' : 's'} selected`
                        : 'No topics selected - showing all posts'
                      }
                    </span>
                    <button
                      onClick={() => setShowTagSelector(false)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Tag Pills (when selector is closed) */}
              {!showTagSelector && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">Popular topics:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {availableTags.slice(0, 6).map((tag) => {
                      const isSelected = selectedTags.includes(tag.slug);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.slug)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                            isSelected
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {tag.name}
                          <span className="ml-1 opacity-75">({tag.count.posts})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <div className="space-y-8">
          {posts.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              {selectedTags.length > 0 ? (
                <div>
                  <p className="text-lg mb-2">
                    No articles found for: {getTagDisplayNames(selectedTags).join(', ')}
                  </p>
                  <button
                    onClick={clearAllFilters}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View all articles
                  </button>
                </div>
              ) : (
                <p>No posts available</p>
              )}
            </div>
          ) : (
            posts.map((post) => (
              <article key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="md:flex">
                  {/* Feature Image or Placeholder */}
                  <div className="md:w-1/3">
                    {post.feature_image ? (
                      <img 
                        src={post.feature_image} 
                        alt={post.feature_image_alt || post.title}
                        className="w-full h-48 md:h-full object-cover cursor-pointer"
                        onClick={() => handleReadMore(post.slug)}
                      />
                    ) : (
                      <div 
                        className="w-full h-48 md:h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center cursor-pointer"
                        onClick={() => handleReadMore(post.slug)}
                      >
                        <div className="text-center text-gray-500">
                          <svg 
                            className="w-12 h-12 mx-auto mb-2" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={1.5} 
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm">No image</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 md:w-2/3">
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.map((tag) => {
                          const isSelected = selectedTags.includes(tag.slug);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(tag.slug)}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                                isSelected
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              }`}
                              title={isSelected ? `Remove ${tag.name} filter` : `Add ${tag.name} filter`}
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag.name}
                              {isSelected && <X className="w-3 h-3 ml-1" />}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Title - Also clickable */}
                    <h2 
                      className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 cursor-pointer hover:text-blue-600 transition-colors duration-200"
                      onClick={() => handleReadMore(post.slug)}
                    >
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 gap-4">
                      {/* Author */}
                      {post.primary_author && (
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          <span>{post.primary_author.name}</span>
                        </div>
                      )}

                      {/* Published Date */}
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{formatDate(post.published_at)}</span>
                      </div>

                      {/* Reading Time */}
                      {post.reading_time > 0 && (
                        <span>{post.reading_time} min read</span>
                      )}
                    </div>

                    {/* Read More Button */}
                    <button
                      onClick={() => handleReadMore(post.slug)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Read Article
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        {/* Load More Button (fallback for mobile or preference) */}
        {hasMore && !loadingMore && (
          <div className="mt-8 text-center">
            <button
              onClick={loadMorePosts}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Load More Articles
            </button>
          </div>
        )}

        {/* Loading More Indicator */}
        {loadingMore && (
          <div className="mt-8 flex justify-center">
            <div className="flex items-center text-gray-600">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              Loading more articles...
            </div>
          </div>
        )}

        {/* End of Posts Message */}
        {!hasMore && posts.length > 0 && (
          <div className="mt-8 text-center text-gray-500">
            <p>You've reached the end of all articles{selectedTags.length > 0 ? ` for: ${getTagDisplayNames(selectedTags).join(', ')}` : ''}</p>
          </div>
        )}

        {/* Posts Info */}
        {pagination && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Showing {posts.length} of {pagination.total} articles
            {selectedTags.length > 0 && ` for: ${getTagDisplayNames(selectedTags).join(', ')}`}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500">
          <p>© 2025 Manuela Bamert Newsletter</p>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;